import React, { useState, useEffect } from "react";
import { 
  Form, 
  Button, 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Modal, 
  Badge, 
  InputGroup, 
  FormControl, 
  Spinner,
  Pagination,
  OverlayTrigger,
  Tooltip,
  Dropdown
} from "react-bootstrap";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from "../../../firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import { 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaCalendarAlt, 
  FaPlus, 
  FaTag, 
  FaRandom, 
  FaExclamationTriangle, 
  FaFilter,
  FaDownload
} from "react-icons/fa";

const Coupons = () => {
  const [couponCode, setCouponCode] = useState("");
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [discount, setDiscount] = useState(""); // Added discount field
  const [discountType, setDiscountType] = useState("percentage"); // percentage or fixed
  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [editCouponId, setEditCouponId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, active, expired
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const couponsPerPage = 10;
  const pageLimit = 5;

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    // First filter by status
    let statusFiltered = [...coupons];
    
    if (filterStatus === "active") {
      const today = new Date().toISOString().split('T')[0];
      statusFiltered = coupons.filter(coupon => 
        coupon.startDate <= today && coupon.endDate >= today
      );
    } else if (filterStatus === "expired") {
      const today = new Date().toISOString().split('T')[0];
      statusFiltered = coupons.filter(coupon => 
        coupon.endDate < today
      );
    } else if (filterStatus === "upcoming") {
      const today = new Date().toISOString().split('T')[0];
      statusFiltered = coupons.filter(coupon => 
        coupon.startDate > today
      );
    }
    
    // Then filter by search term
    if (searchTerm) {
      const filtered = statusFiltered.filter(coupon => 
        coupon.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCoupons(filtered);
    } else {
      setFilteredCoupons(statusFiltered);
    }
    
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [searchTerm, filterStatus, coupons]);

  const fetchCoupons = async () => {
    setLoading(true);
    setError("");
    try {
      const couponsQuery = query(collection(db, "coupons"), orderBy("startDate", "desc"));
      const querySnapshot = await getDocs(couponsQuery);
      const couponsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCoupons(couponsData);
      setFilteredCoupons(couponsData);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setError("Failed to fetch coupons");
      toast.error("Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  };

  const generateCouponCode = () => {
    return `COUPON-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
  };

  const validateCouponForm = () => {
    if ((!autoGenerate && !couponCode) || !startDate || !endDate || !discount) {
      toast.error("Please fill in all required fields");
      return false;
    }
    
    if (new Date(endDate) < new Date(startDate)) {
      toast.error("End date cannot be before start date");
      return false;
    }
    
    if (isNaN(discount) || parseFloat(discount) <= 0) {
      toast.error("Discount must be a positive number");
      return false;
    }
    
    if (discountType === "percentage" && parseFloat(discount) > 100) {
      toast.error("Percentage discount cannot exceed 100%");
      return false;
    }
    
    return true;
  };

  const handleSaveCoupon = async () => {
    if (!validateCouponForm()) return;

    const code = autoGenerate ? generateCouponCode() : couponCode.trim().toUpperCase();
    
    setLoading(true);
    setError("");
    try {
      const couponData = {
        code,
        startDate,
        endDate,
        discount: parseFloat(discount),
        discountType,
        createdAt: new Date().toISOString()
      };
      
      if (editCouponId) {
        const couponRef = doc(db, "coupons", editCouponId);
        await updateDoc(couponRef, couponData);
        toast.success("Coupon updated successfully!");
        setEditCouponId(null);
      } else {
        // Check if coupon with the same code already exists
        const existingCoupon = coupons.find(c => c.code === code);
        if (existingCoupon) {
          toast.error("A coupon with this code already exists");
          setLoading(false);
          return;
        }
        
        await addDoc(collection(db, "coupons"), couponData);
        toast.success("Coupon created successfully!");
      }

      // Reset form fields
      setCouponCode("");
      setStartDate("");
      setEndDate("");
      setDiscount("");
      setDiscountType("percentage");
      setAutoGenerate(true);
      
      fetchCoupons();
    } catch (error) {
      console.error("Error saving coupon:", error);
      setError("Failed to save coupon");
      toast.error("Failed to save coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleEditCoupon = (coupon) => {
    setCouponCode(coupon.code);
    setStartDate(coupon.startDate);
    setEndDate(coupon.endDate);
    setDiscount(coupon.discount?.toString() || "");
    setDiscountType(coupon.discountType || "percentage");
    setAutoGenerate(false);
    setEditCouponId(coupon.id);
    
    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleShowDeleteModal = (coupon) => {
    setCouponToDelete(coupon);
    setShowDeleteModal(true);
  };

  const handleDeleteCoupon = async () => {
    if (!couponToDelete) return;
    
    setLoading(true);
    setError("");
    try {
      const couponRef = doc(db, "coupons", couponToDelete.id);
      await deleteDoc(couponRef);
      toast.success("Coupon deleted successfully!");
      setShowDeleteModal(false);
      fetchCoupons();
    } catch (error) {
      console.error("Error deleting coupon:", error);
      setError("Failed to delete coupon");
      toast.error("Failed to delete coupon");
    } finally {
      setLoading(false);
      setCouponToDelete(null);
    }
  };
  
  const resetForm = () => {
    setCouponCode("");
    setStartDate("");
    setEndDate("");
    setDiscount("");
    setDiscountType("percentage");
    setAutoGenerate(true);
    setEditCouponId(null);
  };
  
  const exportCoupons = () => {
    try {
      const headers = ["Code", "Discount", "Start Date", "End Date", "Status"];
      
      // Convert coupons to CSV
      const csvContent = [
        headers.join(","),
        ...filteredCoupons.map(coupon => {
          const today = new Date().toISOString().split('T')[0];
          let status = "Upcoming";
          if (coupon.startDate <= today && coupon.endDate >= today) {
            status = "Active";
          } else if (coupon.endDate < today) {
            status = "Expired";
          }
          
          return [
            coupon.code,
            `${coupon.discount}${coupon.discountType === 'percentage' ? '%' : ' USD'}`,
            coupon.startDate,
            coupon.endDate,
            status
          ].join(",");
        })
      ].join("\n");
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `coupons_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Coupons exported successfully!");
    } catch (error) {
      console.error("Error exporting coupons:", error);
      toast.error("Failed to export coupons");
    }
  };
  
  // Get coupon status
  const getCouponStatus = (startDate, endDate) => {
    const today = new Date().toISOString().split('T')[0];
    
    if (startDate > today) {
      return { text: "Upcoming", variant: "info" };
    } else if (endDate < today) {
      return { text: "Expired", variant: "danger" };
    } else {
      return { text: "Active", variant: "success" };
    }
  };
  
  // Pagination logic
  const indexOfLastCoupon = currentPage * couponsPerPage;
  const indexOfFirstCoupon = indexOfLastCoupon - couponsPerPage;
  const currentCoupons = filteredCoupons.slice(indexOfFirstCoupon, indexOfLastCoupon);
  const totalPages = Math.ceil(filteredCoupons.length / couponsPerPage);
  
  // Function to render pagination items
  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    let endPage = startPage + pageLimit - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pageLimit + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Row>
              {/* Coupon Form Card */}
              <Col lg={4} className="mb-4">
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 font-weight-bold text-primary">
                      {editCouponId ? (
                        <><FaEdit className="me-2" /> Edit Coupon</>
                      ) : (
                        <><FaPlus className="me-2" /> Create Coupon</>
                      )}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {error && <div className="alert alert-danger">{error}</div>}
                    <Form>
                      <Form.Group className="mb-3" controlId="formAutoGenerate">
                        <Form.Check
                          type="switch"
                          label="Auto-generate coupon code"
                          checked={autoGenerate}
                          onChange={() => setAutoGenerate(!autoGenerate)}
                        />
                      </Form.Group>
                      
                      {!autoGenerate && (
                        <Form.Group className="mb-3" controlId="formCouponCode">
                          <Form.Label>Coupon Code <span className="text-danger">*</span></Form.Label>
                          <InputGroup>
                            <InputGroup.Text><FaTag /></InputGroup.Text>
                            <Form.Control
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="e.g. SUMMER2025"
                            />
                            {!editCouponId && (
                              <Button 
                                variant="outline-primary" 
                                onClick={() => setCouponCode(generateCouponCode())}
                                title="Generate random code"
                              >
                                <FaRandom />
                              </Button>
                            )}
                          </InputGroup>
                          <Form.Text className="text-muted">
                            Code will be converted to uppercase automatically
                          </Form.Text>
                        </Form.Group>
                      )}
                      
                      <Row>
                        <Col sm={6}>
                          <Form.Group className="mb-3" controlId="formDiscountAmount">
                            <Form.Label>Discount Amount <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              step={discountType === "percentage" ? "1" : "0.01"}
                              value={discount}
                              onChange={(e) => setDiscount(e.target.value)}
                              placeholder="Enter amount"
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="mb-3" controlId="formDiscountType">
                            <Form.Label>Discount Type <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                              value={discountType}
                              onChange={(e) => setDiscountType(e.target.value)}
                            >
                              <option value="percentage">Percentage (%)</option>
                              <option value="fixed">Fixed Amount ($)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col sm={6}>
                          <Form.Group className="mb-3" controlId="formStartDate">
                            <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                            <InputGroup>
                              <InputGroup.Text><FaCalendarAlt /></InputGroup.Text>
                              <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Group className="mb-3" controlId="formEndDate">
                            <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                            <InputGroup>
                              <InputGroup.Text><FaCalendarAlt /></InputGroup.Text>
                              <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <div className="d-grid gap-2">
                        <Button 
                          variant="primary" 
                          onClick={handleSaveCoupon} 
                          disabled={loading}
                          className="mt-3"
                        >
                          {loading ? (
                            <><Spinner as="span" animation="border" size="sm" className="me-2" /> Saving...</>
                          ) : editCouponId ? (
                            "Update Coupon"
                          ) : (
                            "Create Coupon"
                          )}
                        </Button>
                        
                        {editCouponId && (
                          <Button 
                            variant="outline-secondary" 
                            onClick={resetForm}
                            className="mt-2"
                          >
                            Cancel Editing
                          </Button>
                        )}
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Coupons List Card */}
              <Col lg={8}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 font-weight-bold text-primary">
                        <FaTag className="me-2" /> Coupons List
                      </h5>
                      {loading && !editCouponId ? (
                        <Spinner animation="border" size="sm" className="ms-3" />
                      ) : (
                        <Badge bg="info" className="ms-2">
                          {filteredCoupons.length}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="d-flex">
                      <InputGroup className="me-3" style={{ width: "200px" }}>
                        <InputGroup.Text>
                          <FaSearch />
                        </InputGroup.Text>
                        <FormControl
                          placeholder="Search coupons..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                      
                      <div className="d-flex">
                        <Dropdown className="me-2">
                          <Dropdown.Toggle variant="outline-secondary" id="dropdown-filter">
                            <FaFilter className="me-2" />
                            {filterStatus === "all" && "All Coupons"}
                            {filterStatus === "active" && "Active"}
                            {filterStatus === "upcoming" && "Upcoming"}
                            {filterStatus === "expired" && "Expired"}
                          </Dropdown.Toggle>
                          
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setFilterStatus("all")}>All Coupons</Dropdown.Item>
                            <Dropdown.Item onClick={() => setFilterStatus("active")}>Active</Dropdown.Item>
                            <Dropdown.Item onClick={() => setFilterStatus("upcoming")}>Upcoming</Dropdown.Item>
                            <Dropdown.Item onClick={() => setFilterStatus("expired")}>Expired</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                        
                        <Button 
                          variant="outline-primary" 
                          onClick={exportCoupons}
                          title="Export Coupons"
                        >
                          <FaDownload />
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  
                  <Card.Body className="p-0">
                    {loading && coupons.length === 0 ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading coupons...</p>
                      </div>
                    ) : filteredCoupons.length > 0 ? (
                      <div className="table-responsive">
                        <Table className="table-hover mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th className="py-3">Code</th>
                              <th className="py-3">Discount</th>
                              <th className="py-3">Date Range</th>
                              <th className="py-3">Status</th>
                              <th className="py-3 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentCoupons.map((coupon) => {
                              const status = getCouponStatus(coupon.startDate, coupon.endDate);
                              
                              return (
                                <tr key={coupon.id}>
                                  <td className="py-3 fw-bold">{coupon.code}</td>
                                  <td className="py-3">
                                    {coupon.discount}
                                    {coupon.discountType === "percentage" ? "%" : " USD"}
                                  </td>
                                  <td className="py-3">
                                    <div className="d-flex flex-column">
                                      <small>
                                        <strong>From:</strong> {coupon.startDate}
                                      </small>
                                      <small>
                                        <strong>To:</strong> {coupon.endDate}
                                      </small>
                                    </div>
                                  </td>
                                  <td className="py-3">
                                    <Badge bg={status.variant}>{status.text}</Badge>
                                  </td>
                                  <td className="py-3">
                                    <div className="d-flex justify-content-center">
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Edit Coupon</Tooltip>}
                                      >
                                        <Button 
                                          variant="outline-primary" 
                                          size="sm" 
                                          className="me-2"
                                          onClick={() => handleEditCoupon(coupon)}
                                          disabled={loading}
                                        >
                                          <FaEdit />
                                        </Button>
                                      </OverlayTrigger>
                                      
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Delete Coupon</Tooltip>}
                                      >
                                        <Button 
                                          variant="outline-danger" 
                                          size="sm"
                                          onClick={() => handleShowDeleteModal(coupon)}
                                          disabled={loading}
                                        >
                                          <FaTrash />
                                        </Button>
                                      </OverlayTrigger>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5 empty-state">
                        <FaExclamationTriangle size={48} className="text-muted mb-3" />
                        <h5>No coupons found</h5>
                        <p className="text-muted">
                          {searchTerm || filterStatus !== "all" 
                            ? "No coupons match your filter criteria." 
                            : "You haven't created any coupons yet."}
                        </p>
                        {(searchTerm || filterStatus !== "all") && (
                          <Button 
                            variant="outline-primary" 
                            onClick={() => {
                              setSearchTerm("");
                              setFilterStatus("all");
                            }}
                          >
                            Clear Filters
                          </Button>
                        )}
                      </div>
                    )}
                  </Card.Body>
                  
                  {filteredCoupons.length > 0 && (
                    <Card.Footer className="bg-white border-0 px-4 py-3">
                      <Row className="align-items-center">
                        <Col>
                          <small className="text-muted">
                            Showing {filteredCoupons.length > 0 ? indexOfFirstCoupon + 1 : 0} to {Math.min(indexOfLastCoupon, filteredCoupons.length)} of {filteredCoupons.length} coupons
                          </small>
                        </Col>
                        <Col className="d-flex justify-content-end">
                          {totalPages > 1 && (
                            <Pagination className="mb-0">
                              <Pagination.First
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                              />
                              <Pagination.Prev
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                              />
                              {renderPaginationItems()}
                              <Pagination.Next
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              />
                              <Pagination.Last
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                              />
                            </Pagination>
                          )}
                        </Col>
                      </Row>
                    </Card.Footer>
                  )}
                </Card>
              </Col>
            </Row>
          </Container>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3 text-danger">
            <FaTrash size={48} />
          </div>
          <h5>Are you sure you want to delete this coupon?</h5>
          {couponToDelete && (
            <p className="text-muted mb-0">
              Coupon Code: <strong>{couponToDelete.code}</strong>
              <br />
              {couponToDelete.discount}{couponToDelete.discountType === "percentage" ? "%" : " USD"} discount
            </p>
          )}
          <p className="text-danger mt-3">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteCoupon}
            disabled={loading}
          >
            {loading ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" /> Deleting...</>
            ) : (
              "Delete Coupon"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Coupons;